<template>
  <div>
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mt-5 pt-5 ps-3">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item">
          <router-link to="/">Home</router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Agents</li>
      </ol>
    </nav>

    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div
              class="card-header d-flex justify-content-between align-items-center"
            >
              <div>
                <h5 class="mb-1">Agents Management</h5>
                <small class="text-muted">
                  Manage AI agents, their configurations, and assignments
                </small>
              </div>
              <button
                @click="router.push('/admin/agents/new')"
                class="btn btn-primary btn-sm"
                :disabled="!canCreate('agents')"
              >
                <i class="fa fa-plus me-1"></i>
                Create Agent
              </button>
            </div>

            <div class="card-body">
              <!-- Search and Filters -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="fa fa-search"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Search agents..."
                      v-model="searchQuery"
                      @input="handleSearch"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <select
                    class="form-select"
                    v-model="filterStatus"
                    @change="loadAgents"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <select
                    class="form-select"
                    v-model="filterModel"
                    @change="loadAgents"
                  >
                    <option value="">All Models</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    <option value="claude-3-opus">Claude 3 Opus</option>
                    <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                  </select>
                </div>
              </div> 
              <!-- Agents Table -->
              <div  class="table-responsive theme-scrollbar">
                <table class="table table-hover">
                  <thead class="table-dark">
                    <tr>
                      <th style="min-width: 200px;">Name</th>
                      <th  style="min-width: 200px;">Slug</th>
                      <th style="min-width: 150px;">Model</th>
                      <th style="min-width: 150px;">Status</th>
                      <th style="min-width: 150px;">Assignments</th>
                      <th style="min-width: 100px;">Tools</th>
                      <th style="min-width: 200px;">Knowledge Bases</th>
                      <th style="min-width: 150px;">Created</th>
                      <th style="min-width: 300px;" class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <!-- Loading State -->
                  <tbody v-if="isLoading">
                   <tr>
                      <td
                      :colspan="9"
                      class="text-center py-4"
                     >
                    <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                   </div>
                       </td>
                   </tr>
                 </tbody>
                  <!-- ampty State -->
                  <tbody  v-else-if="!agents || agents.length === 0">
                   <tr>
                    <td
                      :colspan="9"
                      class="text-center py-4"
                     >
                     <div class="text-center py-5" >
                       <i class="fa fa-robot fa-3x text-muted mb-3"></i>
                       <p class="text-muted">No agents found</p>
                       <button
                       @click="router.push('/admin/agents/new')"
                       class="btn btn-primary btn-sm"
                       :disabled="!canCreate('agents')"
                       >
                         <i class="fa fa-plus me-1"></i>
                         Create Your First Agent
                      </button>
                      </div>
                     </td>
                   </tr>
                 </tbody>
                  <tbody  v-else-if=" agents.length > 0">
                    <tr v-for="agent in agents" :key="agent._id || agent.id">
                      <td>
                        <strong>{{ agent.name }}</strong>
                        <br />
                        <small class="text-muted">{{
                          truncateText(agent.description, 50)
                        }}</small>
                      </td>
                      <td>
                        <code class="text-primary">{{ agent.slug }}</code>
                      </td>
                      <td>
                        <span class="badge bg-info">{{ agent.model }}</span>
                      </td>
                      <td>
                        <span
                          :class="[
                            'badge',
                            agent.is_active ?? agent.isActive
                              ? 'bg-success'
                              : 'bg-secondary',
                          ]"
                        >
                          {{
                            agent.is_active ?? agent.isActive
                              ? "Active"
                              : "Inactive"
                          }}
                        </span>
                      </td>
                      <td>
                        <span class="badge bg-primary">
                          {{
                            agent.assignments_count ||
                            agent.assignmentsCount ||
                            0
                          }}
                        </span>
                      </td>
                      <td>
                        <span class="badge bg-secondary">
                          {{
                            getToolsCount(
                              agent.tools_allowed || agent.toolsAllowed
                            )
                          }}
                        </span>
                      </td>
                      <td>
                        <span class="badge bg-warning text-dark">
                          {{
                            getKnowledgeBasesCount(
                              agent.knowledge_base_ids || agent.knowledgeBaseIds
                            )
                          }}
                        </span>
                      </td>
                      <td>
                        <small>{{
                          formatDate(agent.created_at || agent.createdAt)
                        }}</small>
                      </td>
                      <td class="text-end">
                        <div class="d-flex gap-1 justify-content-end">
                          <button
                            @click="router.push(`/admin/agents/${agent._id || agent.id}`)"
                            class="btn btn-sm btn-outline-primary"
                            title="View Details"
                            :disabled="!canView('agents')"
                          >
                            <i class="fa fa-eye"></i>
                          </button>
                          <button
                            @click="router.push(`/admin/agents/${agent._id || agent.id}/edit`)"
                            class="btn btn-sm  btn-outline-secondary"
                            title="Edit"
                            :disabled="!canUpdate('agents')"
                          >
                            <i class="fa fa-edit"></i>
                          </button>
                          <button
                            @click="toggleAgentStatus(agent)"
                            class="btn btn-sm  btn-outline-warning"
                            :title="
                              agent.is_active ?? agent.isActive
                                ? 'Deactivate'
                                : 'Activate'
                            "
                            :disabled="!canUpdate('agents')"
                          >
                            <i 
                              :class="[
                                'fa',
                                agent.is_active ?? agent.isActive
                                  ? 'fa-pause'
                                  : 'fa-play',
                              ]"
                            ></i>
                          </button>
                          <button
                            @click="confirmDelete(agent)"
                            class="btn btn-sm  btn-outline-danger"
                            title="Delete"
                            :disabled="!canDelete('agents')"
                          >
                            <i class="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div
                v-if="totalPages > 1"
                class="d-flex justify-content-between align-items-center mt-3"
              >
                <div>
                  <small class="text-muted">
                    Showing {{ (currentPage - 1) * pageSize + 1 }} to
                    {{ Math.min(currentPage * pageSize, totalCount) }} of
                    {{ totalCount }} agents
                  </small>
                </div>
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage === 1 }"
                    >
                      <a
                        class="page-link"
                        href="#"
                        @click.prevent="changePage(currentPage - 1)"
                      >
                        Previous
                      </a>
                    </li>
                    <li
                      v-for="page in visiblePages"
                      :key="page"
                      class="page-item"
                      :class="{ active: currentPage === page }"
                    >
                      <a
                        class="page-link"
                        href="#"
                        @click.prevent="changePage(page)"
                      >
                        {{ page }}
                      </a>
                    </li>
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage === totalPages }"
                    >
                      <a
                        class="page-link"
                        href="#"
                        @click.prevent="changePage(currentPage + 1)"
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="deleteModal" tabindex="-1" ref="deleteModalRef">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <p>
              Are you sure you want to delete agent
              <strong>{{ agentToDelete?.name }}</strong
              >?
            </p>
            <p class="text-danger">
              <i class="fa fa-exclamation-triangle me-1"></i>
              This action cannot be undone. All assignments will be removed.
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="deleteAgent"
              :disabled="isDeleting"
            >
              <span v-if="isDeleting">
                <span
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                ></span>
                Deleting...
              </span>
              <span v-else>
                <i class="fa fa-trash me-1"></i>
                Delete
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";
import { Modal } from "bootstrap";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

// Router
const router = useRouter();

// Data
const agents = ref<any[]>([]);
const isLoading = ref(false);
const searchQuery = ref("");
const filterStatus = ref("");
const filterModel = ref("");
const currentPage = ref(1);
const pageSize = ref(10);
const totalCount = ref(0);
const totalPages = ref(0);

// Delete modal
const agentToDelete = ref<any>(null);
const deleteModalRef = ref<HTMLElement | null>(null);
const isDeleting = ref(false);

// Computed
const visiblePages = computed(() => {
  const pages: number[] = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
  const end = Math.min(totalPages.value, start + maxVisible - 1);

  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return pages;
});

// Methods
const loadAgents = async () => {
  isLoading.value = true;
  try {
    const params: Record<string, any> = {
      page: currentPage.value,
      limit: pageSize.value,
    };

    if (searchQuery.value) {
      params.search = searchQuery.value;
    }

    if (filterStatus.value === "active") {
      params.isActive = true;
    } else if (filterStatus.value === "inactive") {
      params.isActive = false;
    }

    if (filterModel.value) {
      params.model = filterModel.value;
    }

    const response = await apiService.getAgents(params);

    if (response) {
      agents.value = response.data || response.agents || [];
      totalCount.value = response.total || response.totalCount || 0;
      totalPages.value = Math.ceil(totalCount.value / pageSize.value);
    }
  } catch (err: any) {
    console.error("Error loading agents:", err);
    showToast(err?.response?.data?.message || "Failed to load agents", "error");
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = () => {
  currentPage.value = 1;
  loadAgents();
};

const changePage = (page: number) => {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  loadAgents();
};

const toggleAgentStatus = async (agent: any) => {
  
  const currentStatus = agent.is_active ?? agent.isActive;
  const newStatus = !currentStatus;
  const action = newStatus ? "activate" : "deactivate";
  const payload = {
    is_active: newStatus,
  }

  if (!confirm(`Are you sure you want to ${action} agent "${agent.name}"?`)) {
    return;
  }

  try {
    await apiService.updateAgent(agent._id || agent.id, payload);
    showToast(`Agent ${action}d successfully`, "success");
    loadAgents();
  } catch (err: any) {
    console.error(`Error toggling agent status:`, err);
    showToast(
      err?.response?.data?.message || `Failed to ${action} agent`,
      "error"
    );
  }
};

const confirmDelete = (agent: any) => {
  agentToDelete.value = agent;
  // Use Bootstrap modal
  const modalElement = deleteModalRef.value;
  if (modalElement) {
    const modal = new Modal(modalElement);
    modal.show();
  }
};

const deleteAgent = async () => {
  if (!agentToDelete.value) return;

  isDeleting.value = true;
  try {
    await apiService.deleteAgent(
      agentToDelete.value._id || agentToDelete.value.id
    );
    showToast("Agent deleted successfully", "success");

    // Close modal
    const modalElement = deleteModalRef.value;
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    // Reload agents
    agentToDelete.value = null;
    loadAgents();
  } catch (err: any) {
    console.error("Error deleting agent:", err);
    showToast(
      err?.response?.data?.message || "Failed to delete agent",
      "error"
    );
  } finally {
    isDeleting.value = false;
  }
};

const truncateText = (text: string, maxLength: number): string => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const getToolsCount = (tools: any): number => {
  if (!tools) return 0;
  if (Array.isArray(tools)) return tools.length;
  return 0;
};

const getKnowledgeBasesCount = (kbIds: any): number => {
  if (!kbIds) return 0;
  if (Array.isArray(kbIds)) return kbIds.length;
  return 0;
};

const showToast = (message: string, type: "success" | "error" | "info") => {
  console.log(`[${type.toUpperCase()}] ${message}`);
  // Integrate with your toast library here
};

// Lifecycle
onMounted(() => {
  loadAgents();
});
</script>

<style scoped>
.breadcrumb-item a {
  text-decoration: none;
  color: #6c757d;
}

.breadcrumb-item a:hover {
  color: #495057;
}

.table th { 
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
}

code {
  font-size: 0.875rem;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  background-color: #f8f9fa;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
}
</style>
